<template>
  <form>
    <vl-properties-list>
      <vl-properties-label>{{ $t('productentity.detail.id') }}</vl-properties-label>
      <vl-properties-data>{{ detail.id }}</vl-properties-data>
    </vl-properties-list>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="code"
        :label="t('productentity.detail.code')"
        :placeholder="t('productentity.detail.code')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('productentity.detail.label')"
        :placeholder="t('productentity.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('productentity.detail.description')"
        :placeholder="t('productentity.detail.description')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="model"
        :label="t('productentity.detail.model')"
        :placeholder="$t('productentity.detail.model')"
        :options="entityModels"
        :custom-label="entityModelLabel"
        :sortFunction="entityModelSortFunction"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-if="!isCodeListOrTable" v-vl-spacer:bottom.small>
      <PbsSelectField
        name="crs"
        :label="t('productentity.detail.crs')"
        :placeholder="$t('productentity.detail.crs')"
        :options="sridTypes"
        :custom-label="(e) => e.name"
      />
    </vl-form-column>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { ProductEntityDetailDto, SearchEntityModelDto, EnumerationDto } from '@/api/backoffice/clients';
import { useForm } from 'vee-validate';
import { object, string, mixed } from 'yup';
import { useI18n } from 'vue-i18n';
import { useProductEntityStore } from '@/modules/configuration/store/product-entity/product-entity.store';
import { useEntityModelSelectStore } from '@/modules/configuration/store/entity-model/entity-model-select.store';
import { ENUM_SridTypes } from '@/modules/configuration/common';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import { entityModelLabel, entityModelSortFunction } from '@/modules/configuration/common';
import { RegularExpressions, noSurroundingSpaces } from '@/helpers';
import { ENUM_EntityTypes } from '@/modules/configuration/common';

const { t } = useI18n();

const productEntityStore = useProductEntityStore();
const entityModelSelectStore = useEntityModelSelectStore();

const props = defineProps<{
  detail: ProductEntityDetailDto;
  specificationId: number;
}>();

const validationSchema = object({
  label: string()
    .required(t('validations.messages.required', { _field_: t('productentity.detail.label') }))
    .test(noSurroundingSpaces(t('productentity.detail.label')))
    .max(256, t('validations.messages.maxChar', { _field_: t('productentity.detail.label'), length: 256 })),
  code: string()
    .required(t('validations.messages.required', { _field_: t('productentity.detail.code') }))
    .max(64, t('validations.messages.maxChar', { _field_: t('productentity.detail.code'), length: 64 }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('productentity.detail.code') })
    ),
  description: string().required(
    t('validations.messages.required', { _field_: t('productentity.detail.description') })
  ),
  model: object().required(t('validations.messages.required', { _field_: t('productentity.detail.model') })),
  custom: mixed().test(
            'custom-validation',
            'The properties do not meet the validation criteria',
            function (_, context) {
                const { crs
                } = context.parent;
                           
                if (!crs && !isCodeListOrTable.value) {
                  return context.createError({
                    path: 'crs',
                    message: t('validations.messages.required', {
                      _field_: t('productentity.detail.crs')
                    })
                  });  
                }

                return true;
            }
        )
});

const { resetForm, handleSubmit, values } = useForm({
  validationSchema
});

const detail = computed((): ProductEntityDetailDto => {
  return props.detail;
});

const sridTypes = computed((): EnumerationDto[] => {
  return Object.values(ENUM_SridTypes);
});

const fetchingModels = computed((): Boolean => {
  return entityModelSelectStore.loading;
});

const entityModels = computed((): SearchEntityModelDto[] => {
  return entityModelSelectStore.items ?? [];
});

const entityModel = computed(() => {
  return values.model;
});

const isCodeListOrTable = computed((): boolean => {
  return entityModel.value?.entityTypeName == ENUM_EntityTypes.CodeList.name || entityModel.value?.entityTypeName == ENUM_EntityTypes.Table.name;
});

onMounted(async () => {
  await entityModelSelectStore.getModels({ force: true, contextTypeName: 'Product' });

  resetForm({
    values: {
      code: detail.value.code,
      label: detail.value.label,
      description: detail.value.description,
      crs: detail.value.crs,
      model: detail.value.model
    }
  });
});

const onSuccess = async (values) => {
  const updateDto = {
    productSpecificationId: props.specificationId,
    label: values.label,
    code: values.code,
    description: values.description,
    modelId: values.model.id,
    crs: values.crs ? values.crs.name : 'Lambert72' // TODO: fix default crs
  };

  await productEntityStore.update(props.specificationId, detail.value.id, updateDto);
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
